import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { useScrollEnd } from "@/hooks/use-scroll-end";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import { matchSorter } from "match-sorter";
import type React from "react";
import {
    Children,
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useMemo,
    useRef,
    type FC,
    type PropsWithChildren,
} from "react";
import { createPortal } from "react-dom";
import {
    DismissButton,
    FocusScope,
    HiddenSelect,
    useButton,
    useInteractOutside,
    useListBox,
    useOption,
    useOverlayPosition,
    useSelect,
} from "react-aria";
import type { AriaListBoxOptions } from "react-aria";
import { Item, useSelectState, type SelectState } from "react-stately";
import type { Placement } from "@react-types/overlays";
import type { Node } from "@react-types/shared";

/**
 * Component displayName constants for Fast Refresh compatibility.
 * Using displayName for component identification ensures components are recognized
 * correctly even after Fast Refresh updates, as displayName strings remain stable
 * while function references may change.
 */
const COMBOBOX_VALUE = "Combobox.Value";
const COMBOBOX_LIST = "Combobox.List";
const COMBOBOX_POPOVER = "Combobox.Popover";
const COMBOBOX_LOADING = "Combobox.Loading";
const COMBOBOX_EMPTY = "Combobox.Empty";
const COMBOBOX_SEARCH_BOX = "Combobox.SearchBox";
const COMBOBOX_LIST_ITEM_LEADING_ICON = "Combobox.ListItemLeadingIcon";
const COMBOBOX_LIST_ITEM_TITLE = "Combobox.ListItemTitle";
const COMBOBOX_LIST_ITEM_DESCRIPTION = "Combobox.ListItemDescription";
const COMBOBOX_FOOTER = "Combobox.Footer";

const NAVIGATION_KEYS = ["ArrowDown", "ArrowUp", "Home", "End", "Enter"];

type ComboboxAtoms = {
    filterAtom: ReturnType<typeof atom<string>>;
};

type ComboboxContext = {
    atoms: ComboboxAtoms;
    state: SelectState<object>;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
    popoverRef: React.RefObject<HTMLDivElement | null>;
    listBoxRef: React.MutableRefObject<HTMLUListElement | null>;
    renderedItems: Map<string, React.ReactNode>;
    onOpenChange: (open: boolean) => void;
    filterPlaceholder: string;
    /** Props from useSelect to spread on the value display element */
    valueProps: React.HTMLAttributes<HTMLElement>;
    /** Props from useSelect to spread on the listbox element */
    menuProps: AriaListBoxOptions<object>;
};

const ComboboxContext = createContext<ComboboxContext | null>(null);

function useComboboxContext(componentName: string): ComboboxContext {
    const ctx = useContext(ComboboxContext);
    if (!ctx) {
        throw new Error(`${componentName} must be used within a Combobox`);
    }
    return ctx;
}

const getComponentDisplayName = (element: React.ReactElement): string | undefined => {
    const type = element.type as React.ComponentType;
    return type.displayName;
};

function extractValueFromListItem(element: React.ReactElement): string | undefined {
    if (getComponentDisplayName(element) === "Combobox.ListItem") {
        return (element.props as { value?: string }).value;
    }
    return undefined;
}

/**
 * Props for the Combobox.List component.
 *
 * @template T - The type of items in the list
 */
export type ComboboxListProps<T> = {
    /** Array of items to display */
    items: T[];
    /** Keys to search on to filter the list. Supports dot notation for nested keys. */
    searchKeys: string[];

    /** Render function for each item */
    children: (item: T) => React.ReactNode;
    /** Custom component to show when no results are found */
    noMatchesComponent?: React.ReactNode;
    /** Disable filtering */
    disableFiltering?: boolean;
    /** Callback function that gets triggered when the user scrolls to the end of the list */
    onScrollEnd?: () => void;
    /** Threshold in pixels from the bottom to trigger onScrollEnd (default: 100) */
    scrollEndThreshold?: number;
    /** Additional CSS class name */
    className?: string;
};

/**
 * Props for the root Combobox component.
 *
 * The root component of the combobox. Any sub-components must be rendered as children of this component.
 */
export type ComboboxProps = PropsWithClassName & {
    /**
     * The default value of the combobox for uncontrolled usage.
     */
    defaultValue?: string;

    /**
     * The current value of the combobox.
     * Use in combination with `onValueChange` to make the combobox controlled.
     */
    value?: string;

    /**
     * A callback function that is called with the selected value when the value of the combobox changes.
     */
    onValueChange?: (value: string) => void;

    /**
     * Called when the popover opens or closes.
     */
    onOpenChange?: (open: boolean) => void;

    /**
     * The placeholder text for the search box in the combobox.
     * Defaults to "Search" if unspecified.
     */
    filterPlaceholder?: string;

    /**
     * Whether the combobox is in loading state. Default is false.
     * If true, the combobox will be disabled and a loading spinner will be displayed.
     *
     * The label of the dropdown will be replaced with the loading label,
     * which is inferred from Combobox.Loading if it is provided as a child.
     * Otherwise, it will default to "Loading…".
     */
    loading?: boolean;

    /**
     * Whether the combobox is disabled. Default is false.
     * If true, the combobox will be disabled and the dropdown will not be openable.
     */
    disabled?: boolean;

    /**
     * Accessible label for the combobox. If not provided, inferred from
     * filterPlaceholder or the selected value, falling back to "Select an option".
     */
    "aria-label"?: string;
};

/**
 * Available slot components for the Combobox.
 */
type ComboboxSlots = {
    /**
     * Renders a popover for the combobox with a search box automatically inserted.
     * If not provided, all the components that belong in the popover will be automatically wrapped in this component.
     * If you want to customize the popover, for example to override the default width of the popover, pass this component as a child
     * to the Combobox component, then put all the components that belong in the popover in this component.
     *
     * By default, the popover will be the same width as the trigger.
     *
     * **Slots:**
     * - Combobox.List
     * - Combobox.Empty
     * - Combobox.Loading
     */
    Popover: typeof ComboboxPopover;

    /**
     * Customizes how the selected value is displayed using either a render function or a react node.
     * It renders `children` in the dropdown trigger. You must wrap whatever you want to render in the trigger in this component.
     *
     * If rendered as is without any child, the selected value will be displayed using ValueLabel.
     */
    Value: typeof ComboboxValue;

    /**
     * Default styled label for the selected value.
     * Usually rendered as a child of Value.
     */
    ValueLabel: typeof ComboboxValueLabel;

    /**
     * Renders a list of selectable items with filtering.
     */
    List: typeof ComboboxList;

    /**
     * Individual item in the list. When selected, a checkmark icon will be displayed at the end (right hand side) of the item.
     */
    ListItem: typeof ComboboxListItem;

    /**
     * Styled title text for the list item.
     */
    ListItemTitle: typeof ComboboxListItemTitle;

    /**
     * Styled description text for list items.
     */
    ListItemDescription: typeof ComboboxListItemDescription;

    /**
     * Container for an icon at the start of a list item.
     */
    ListItemLeadingIcon: typeof ComboboxListItemLeadingIcon;

    /**
     * Styled empty label for the combobox that can be used to display a message when there are no options to display.
     *
     * Usually:
     * - passed as a child to either Combobox or Combobox.Popover when there are no options to display; or
     * - passed as the `noMatchesComponent` prop to the Combobox.List component when the options are all filtered out.
     *
     * You can have both in conjunction!
     */
    Empty: typeof ComboboxEmpty;

    /**
     * Styled loading label for the combobox that can be used to display a message when the combobox is loading.
     * Usually passed as a child to the Combobox component.
     */
    Loading: typeof ComboboxLoading;

    /**
     * Search input for filtering the combobox options.
     * Can be controlled via value/onValueChange props or used uncontrolled.
     * Shows a loading spinner when loading prop is true.
     *
     * Should be placed inside Combobox.Popover or directly under Combobox.
     */
    SearchBox: typeof ComboboxSearchBox;

    /**
     * Footer for the combobox.
     */
    Footer: typeof ComboboxFooter;
};

/**
 * Props for the Combobox.Popover component.
 */
export type ComboboxPopoverProps = PropsWithChildren &
    PropsWithClassName & {
        /** The placeholder text for the search box in the popover */
        filterPlaceholder?: string;
        /**
         * Whether the popover should match the trigger width. Defaults to true.
         * When false, you can use min-w and max-w classes to control the width.
         */
        sameWidth?: boolean;
        /**
         * Whether the popover should flip to the opposite side when it overflows.
         * @default true
         */
        flip?: boolean;
        /**
         * The placement of the popover relative to the trigger.
         * @default "bottom start"
         */
        placement?: Placement;
    };

/**
 * Props for the Combobox.ListItem component.
 */
export type ComboboxListItemProps = {
    /** The value of this item, used as the selection key */
    value: string;
    /** Shorthand for simple text items */
    title?: React.ReactNode;
    /** Children */
    children?: React.ReactNode;
    /** Additional CSS class name */
    className?: string;
    disabled?: boolean;
};

/**
 * Props for the Combobox.ListItemLeadingIcon component.
 */
export type ComboboxListItemLeadingIconProps = React.ComponentProps<"span">;

/**
 * Props for the Combobox.Empty component.
 */
export type ComboboxEmptyProps = React.ComponentProps<typeof Text>;

/**
 * Props for the Combobox.Loading component.
 */
export type ComboboxLoadingProps = React.ComponentProps<typeof Text>;

/**
 * Props for the Combobox.SearchBox component.
 */
export type ComboboxSearchBoxProps = {
    /**
     * Callback when the internal search value has changed.
     */
    onValueChanged?: (value: string) => void;

    /**
     * Whether the search is in a loading state.
     * If true, a loading spinner will be displayed.
     */
    loading?: boolean;

    /** Placeholder text for the search input */
    placeholder?: string;

    /** Additional CSS class name */
    className?: string;
};

type ParsedSlots = {
    selectedValueSlot: React.ReactNode | null;
    listElement: React.ReactElement | null;
    popoverElement: React.ReactElement | null;
    loadingComponent: React.ReactNode | null;
    loadingLabel: React.ReactNode | null;
    emptyComponent: React.ReactNode | null;
    footerComponent: React.ReactNode | null;
};

function parseSlots(children: React.ReactNode): ParsedSlots {
    const result: ParsedSlots = {
        selectedValueSlot: null,
        listElement: null,
        popoverElement: null,
        loadingComponent: null,
        loadingLabel: null,
        emptyComponent: null,
        footerComponent: null,
    };
    Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;
        const dn = getComponentDisplayName(child);
        switch (dn) {
            case COMBOBOX_VALUE:
                result.selectedValueSlot = child;
                break;
            case COMBOBOX_LIST:
                result.listElement = child;
                break;
            case COMBOBOX_LOADING:
                result.loadingComponent = child;
                result.loadingLabel = (child.props as { children?: React.ReactNode }).children || "Loading…";
                break;
            case COMBOBOX_POPOVER:
                result.popoverElement = child;
                Children.forEach(
                    (child.props as { children?: React.ReactNode }).children,
                    (nested: React.ReactNode) => {
                        if (!isValidElement(nested)) return;
                        const ndn = getComponentDisplayName(nested);
                        if (ndn === COMBOBOX_LIST) result.listElement = nested;
                        if (ndn === COMBOBOX_LOADING) {
                            result.loadingLabel =
                                (nested.props as { children?: React.ReactNode }).children || "Loading…";
                        }
                    },
                );
                break;
            case COMBOBOX_EMPTY:
                result.emptyComponent = child;
                break;
            case COMBOBOX_FOOTER:
                result.footerComponent = child;
                break;
        }
    });
    return result;
}

/**
 * A searchable select component that combines a text input with a dropdown list.
 * Internally powered by react-aria's useSelectState, useListBox, and useOption hooks.
 *
 * ## Features
 * - **Searchable**: Filter options by typing in the input field
 * - **Keyboard Navigation**: Full keyboard support for accessibility
 * - **Customizable**: Flexible composition with sub-components
 * - **Empty State**: Configurable empty state when no results are found
 * - **Controlled/Uncontrolled**: Supports both controlled and uncontrolled modes
 *
 * @example
 * ```tsx
 * <Combobox defaultValue="Apple">
 *   <Combobox.List
 *     items={items}
 *     searchKeys={["name", "category"]}
 *     noMatchesComponent={<Combobox.Empty>No fruits found</Combobox.Empty>}
 *   >
 *     {(item) => <Combobox.ListItem key={item.id} value={item.name} title={item.name} />}
 *   </Combobox.List>
 * </Combobox>
 * ```
 */
const Combobox: FC<PropsWithChildren<ComboboxProps>> & ComboboxSlots = ({
    children,
    defaultValue,
    value,
    onValueChange,
    onOpenChange,
    filterPlaceholder = "Search",
    loading = false,
    disabled = false,
    className,
    "aria-label": ariaLabelProp,
}) => {
    const atoms = useMemo<ComboboxAtoms>(() => ({ filterAtom: atom("") }), []);
    const setFilter = useSetAtom(atoms.filterAtom);
    const filterValue = useAtomValue(atoms.filterAtom);

    const slots = parseSlots(children);
    const { listElement, popoverElement, loadingComponent, loadingLabel, emptyComponent, footerComponent } = slots;
    const selectedValueSlot = slots.selectedValueSlot ?? <ComboboxValue />;

    const listProps = listElement ? (listElement.props as ComboboxListProps<unknown>) : undefined;
    const renderItem = listProps?.children;

    const filteredItems = useMemo(() => {
        const allItems = listProps?.items ?? [];
        const searchKeys = listProps?.searchKeys ?? [];
        const disableFiltering = listProps?.disableFiltering ?? false;
        if (!filterValue || disableFiltering) return allItems;
        const terms = filterValue.split(" ");
        return terms.reduceRight((results, term) => matchSorter(results, term, { keys: searchKeys }), allItems);
    }, [filterValue, listProps?.items, listProps?.searchKeys, listProps?.disableFiltering]);

    const { itemKeys, renderedItems } = useMemo(() => {
        const keys: string[] = [];
        const rendered = new Map<string, React.ReactNode>();
        for (const item of filteredItems) {
            if (!renderItem) continue;
            const element = renderItem(item);
            if (isValidElement(element)) {
                const val = extractValueFromListItem(element);
                if (val !== undefined) {
                    keys.push(val);
                    rendered.set(val, element);
                }
            }
        }
        return { itemKeys: keys, renderedItems: rendered };
    }, [filteredItems, renderItem]);

    const itemElements = useMemo(
        () =>
            itemKeys.map((key) => (
                <Item key={key} textValue={key}>
                    {key}
                </Item>
            )),
        [itemKeys],
    );

    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const listBoxRef = useRef<HTMLUListElement>(null);
    const dialogOverflowRef = useRef<string | undefined>(undefined);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) setFilter("");

            // HACK: Radix dialog clips the popover due to overflow-x-auto implicitly
            // setting overflow-y: auto. Temporarily override when open. Remove when Radix is replaced.
            const dialog = triggerRef.current?.closest("[role='dialog']") as HTMLElement | null;
            if (dialog) {
                if (isOpen) {
                    dialogOverflowRef.current = dialog.style.overflow;
                    dialog.style.overflow = "visible";
                } else {
                    dialog.style.overflow = dialogOverflowRef.current ?? "";
                    dialogOverflowRef.current = undefined;
                }
            }

            onOpenChange?.(isOpen);
        },
        [onOpenChange, setFilter],
    );

    const state = useSelectState({
        children: itemElements as unknown as React.ReactElement,
        selectedKey: value ?? undefined,
        defaultSelectedKey: defaultValue ?? undefined,
        onSelectionChange: (key) => {
            if (key !== null && key !== undefined) {
                onValueChange?.(String(key));
            }
            state.close();
        },
        onOpenChange: handleOpenChange,
    });

    const selectAriaLabel = ariaLabelProp ?? filterPlaceholder ?? value ?? "Select an option";

    const { triggerProps, valueProps, menuProps } = useSelect(
        { "aria-label": selectAriaLabel, isDisabled: loading || disabled },
        state,
        triggerRef,
    );

    const { buttonProps } = useButton(triggerProps, triggerRef);

    useInteractOutside({
        ref: triggerRef,
        isDisabled: !state.isOpen,
        onInteractOutside: (e) => {
            if (popoverRef.current?.contains(e.target as globalThis.Node)) return;
            state.close();
        },
    });

    let popover: React.ReactNode;
    if (popoverElement) {
        popover = popoverElement;
    } else {
        popover = (
            <ComboboxPopover>
                <ComboboxSearchBox placeholder={filterPlaceholder} />
                {loadingComponent}
                {emptyComponent}
                {listElement}
                {footerComponent}
            </ComboboxPopover>
        );
    }

    return (
        <ComboboxContext.Provider
            value={{
                atoms,
                state,
                triggerRef,
                popoverRef,
                listBoxRef,
                renderedItems,
                onOpenChange: (open) => (open ? state.open() : state.close()),
                filterPlaceholder,
                valueProps,
                menuProps,
            }}
        >
            <HiddenSelect state={state} triggerRef={triggerRef} />
            <button
                {...buttonProps}
                ref={triggerRef}
                className={cn(
                    "flex h-9 min-w-40 items-center justify-between gap-2 rounded-lg border border-border-input-default bg-surface-input px-3",
                    "transition-all duration-150 ease-out",
                    "outline-none",
                    "focus:border-border-input-active focus:ring-1 focus:ring-border-brand focus:ring-offset-1",
                    "aria-expanded:border-border-input-active aria-expanded:ring-4 aria-expanded:ring-ring-default aria-expanded:ring-offset-0",
                    { "cursor-not-allowed opacity-50": loading || disabled },
                    className,
                )}
            >
                <span className="truncate">
                    {loading ? <ComboboxValueLabel>{loadingLabel}</ComboboxValueLabel> : selectedValueSlot}
                </span>
                {loading ? (
                    <IconSpinner className="animate-spin text-content-primary" size="sm" />
                ) : (
                    <IconChevronDownSmall size={20} aria-hidden="true" />
                )}
            </button>
            {state.isOpen && popover}
        </ComboboxContext.Provider>
    );
};

const ComboboxPopover: FC<ComboboxPopoverProps> = ({
    children,
    className,
    sameWidth = true,
    flip = true,
    placement = "bottom start",
}) => {
    const ctx = useComboboxContext("ComboboxPopover");
    const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: ctx.triggerRef,
        overlayRef: ctx.popoverRef,
        placement,
        offset: 4,
        isOpen: true,
        shouldFlip: flip,
        shouldUpdatePosition: true,
        onClose: null,
    });

    const triggerWidth = ctx.triggerRef.current?.offsetWidth;

    let hasSearchBox = false;
    Children.forEach(children, (child) => {
        if (isValidElement(child) && getComponentDisplayName(child) === COMBOBOX_SEARCH_BOX) {
            hasSearchBox = true;
        }
    });

    const portalContainer = ctx.triggerRef.current?.closest("[role='dialog']") ?? document.body;

    return createPortal(
        <FocusScope restoreFocus>
            <div
                {...positionProps}
                ref={ctx.popoverRef as React.RefObject<HTMLDivElement>}
                role="presentation"
                style={
                    {
                        ...positionProps.style,
                        "--popover-anchor-width": triggerWidth != null ? `${triggerWidth}px` : undefined,
                        ...(sameWidth && triggerWidth ? { width: triggerWidth } : {}),
                    } as React.CSSProperties
                }
                className={cn(
                    "z-[999]",
                    "overflow-auto rounded-lg border border-border-base bg-surface-popover shadow",
                    "origin-top",
                    className,
                )}
                onMouseDown={(e) => {
                    // Keep DOM focus on the search input; virtual focus handles list highlighting.
                    if ((e.target as HTMLElement).tagName !== "INPUT") {
                        e.preventDefault();
                    }
                }}
            >
                <DismissButton onDismiss={() => ctx.onOpenChange(false)} />
                {!hasSearchBox && <ComboboxSearchBox placeholder={ctx.filterPlaceholder} />}
                {children}
                <DismissButton onDismiss={() => ctx.onOpenChange(false)} />
            </div>
        </FocusScope>,
        portalContainer,
    );
};
ComboboxPopover.displayName = COMBOBOX_POPOVER;

const ComboboxSearchBox: FC<ComboboxSearchBoxProps> = ({ placeholder, onValueChanged, loading = false, className }) => {
    const ctx = useComboboxContext("ComboboxSearchBox");
    const { atoms, state, listBoxRef, filterPlaceholder } = ctx;
    const [filterValue, setFilterValue] = useAtom(atoms.filterAtom);
    const resolvedPlaceholder = placeholder ?? filterPlaceholder;

    const inputRef = useCallback((node: HTMLInputElement | null) => {
        node?.focus({ preventScroll: true });
    }, []);

    const searchValueEffect = useMemo(
        () =>
            onValueChanged
                ? atomEffect((get) => {
                      const value = get(atoms.filterAtom);
                      onValueChanged(value);
                  })
                : atom(null),
        [atoms.filterAtom, onValueChanged],
    );
    useAtom(searchValueEffect);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            state.close();
            return;
        }

        if (NAVIGATION_KEYS.includes(e.key)) {
            if (e.key !== "Enter") {
                e.preventDefault();
            }
            e.stopPropagation();

            const listBox = listBoxRef.current;
            if (!listBox) return;

            // Enter targets the focused item for selection; other keys target the listbox.
            if (e.key === "Enter") {
                const focusedItem = listBox.querySelector<HTMLElement>("[data-active-item='true']");
                (focusedItem ?? listBox).dispatchEvent(new KeyboardEvent(e.nativeEvent.type, e.nativeEvent));
            } else {
                listBox.dispatchEvent(new KeyboardEvent(e.nativeEvent.type, e.nativeEvent));
            }
        }
    };

    return (
        <div className="relative flex h-9 w-full items-center gap-2 border-b border-border-base px-2.5">
            <IconMagnifyingGlass className="-translate-y-[0.5px] text-content-muted" size={20} />
            <input
                ref={inputRef}
                type="text"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={resolvedPlaceholder}
                className={cn(
                    "h-9 w-full max-w-full -translate-y-px bg-transparent py-3 text-base text-content-primary placeholder:translate-y-px placeholder:text-base placeholder:text-content-muted focus:outline-none focus:ring-0",
                    loading ? "pr-8" : "",
                    className,
                )}
            />
            <span
                className={cn(
                    "absolute right-2.5 transition-opacity duration-150 ease-in",
                    loading ? "opacity-100" : "opacity-0",
                )}
            >
                <IconSpinner className="animate-spin text-content-muted" size="sm" />
            </span>
        </div>
    );
};
ComboboxSearchBox.displayName = COMBOBOX_SEARCH_BOX;

const ComboboxOption: FC<{ node: Node<object>; renderedContent: React.ReactNode | undefined }> = ({
    node,
    renderedContent,
}) => {
    const { state } = useComboboxContext("ComboboxOption");
    const ref = useRef<HTMLLIElement>(null);
    const { optionProps, isSelected, isFocused, isDisabled } = useOption(
        { key: node.key, shouldUseVirtualFocus: true },
        state,
        ref,
    );

    let content: React.ReactNode = null;
    if (isValidElement(renderedContent)) {
        content = buildListItemContent(renderedContent.props as ComboboxListItemProps);
    }

    return (
        <li
            {...optionProps}
            ref={ref}
            data-active-item={isFocused || undefined}
            className={cn(
                "group/combobox-item flex cursor-pointer select-none items-center justify-between pb-1 text-base text-content-primary first:pt-0 last:pb-0 scroll-mt-1 last:scroll-mb-1",
                isDisabled && "cursor-not-allowed opacity-50",
            )}
        >
            <div
                className={cn(
                    "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5",
                    isFocused && "bg-surface-hover",
                    "group-hover/combobox-item:bg-surface-hover",
                )}
            >
                <div className="min-w-0 flex-1">{content}</div>
                {isSelected && (
                    <span className="flex-none">
                        <IconCheckmark2Small className="size-4" />
                    </span>
                )}
            </div>
        </li>
    );
};

function buildListItemContent(props: ComboboxListItemProps): React.ReactNode {
    const { children, title } = props;
    if (children) {
        let leadingIcon: React.ReactNode | null = null;
        let titleSlot: React.ReactNode | null = null;
        let description: React.ReactNode | null = null;
        Children.forEach(children, (child) => {
            if (isValidElement(child)) {
                const dn = getComponentDisplayName(child);
                switch (dn) {
                    case COMBOBOX_LIST_ITEM_LEADING_ICON:
                        if ((child.props as { children?: React.ReactNode }).children) leadingIcon = child;
                        break;
                    case COMBOBOX_LIST_ITEM_TITLE:
                        titleSlot = child;
                        break;
                    case COMBOBOX_LIST_ITEM_DESCRIPTION:
                        description = child;
                        break;
                }
            }
        });
        if (titleSlot && description) {
            return (
                <div className="flex items-center">
                    {leadingIcon}
                    <div className="flex flex-col">
                        {titleSlot}
                        {description}
                    </div>
                </div>
            );
        }
        if (leadingIcon && titleSlot) {
            return (
                <div className="flex min-w-0 items-center gap-1.5">
                    {leadingIcon}
                    <div className="min-w-0 flex-1">{titleSlot}</div>
                </div>
            );
        }
        return children;
    }
    if (title && typeof title === "string") {
        return <ComboboxListItemTitle>{title}</ComboboxListItemTitle>;
    }
    return null;
}

const ComboboxList = <T,>({
    noMatchesComponent,
    onScrollEnd,
    scrollEndThreshold = 100,
    className,
}: ComboboxListProps<T>) => {
    const ctx = useComboboxContext("ComboboxList");
    const { state, renderedItems, menuProps, listBoxRef } = ctx;
    const internalRef = useRef<HTMLUListElement>(null);
    const { listBoxProps } = useListBox(
        {
            ...menuProps,
            autoFocus: "first",
            shouldUseVirtualFocus: true,
            shouldFocusOnHover: true,
        },
        state,
        internalRef,
    );

    const { scrollContainerRef, onScroll: handleScroll } = useScrollEnd({
        onScrollEnd,
        threshold: scrollEndThreshold,
    });

    const collectionItems = [...state.collection];
    const isEmpty = collectionItems.length === 0;

    return (
        <ul
            {...listBoxProps}
            ref={(node) => {
                (internalRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
                listBoxRef.current = node;
                (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current =
                    node as unknown as HTMLDivElement;
            }}
            className={cn("h-auto max-h-[320px] overflow-auto", isEmpty ? "p-0" : "p-1", className)}
            onScroll={handleScroll}
        >
            {isEmpty
                ? noMatchesComponent || null
                : collectionItems.map((node) => (
                      <ComboboxOption
                          key={node.key}
                          node={node}
                          renderedContent={renderedItems.get(String(node.key))}
                      />
                  ))}
        </ul>
    );
};
ComboboxList.displayName = COMBOBOX_LIST;

const ComboboxListItem: FC<ComboboxListItemProps> = (_props) => {
    return null;
};
ComboboxListItem.displayName = "Combobox.ListItem";

const ComboboxListItemTitle: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => (
    <Text className={cn("truncate text-content-primary", className)} {...props}>
        {children}
    </Text>
);
ComboboxListItemTitle.displayName = COMBOBOX_LIST_ITEM_TITLE;

const ComboboxListItemDescription: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => (
    <Text className={cn("text-sm text-content-secondary", className)} {...props}>
        {children}
    </Text>
);
ComboboxListItemDescription.displayName = COMBOBOX_LIST_ITEM_DESCRIPTION;

const ComboboxListItemLeadingIcon: FC<ComboboxListItemLeadingIconProps> = ({ children, className, ...props }) => {
    if (!children) return null;
    return (
        <span className={cn("mr-2", className)} {...props}>
            {children}
        </span>
    );
};
ComboboxListItemLeadingIcon.displayName = COMBOBOX_LIST_ITEM_LEADING_ICON;

const ComboboxEmpty: FC<ComboboxEmptyProps> = ({ children, className, ...props }) => (
    <Text className={cn("select-none px-3 py-2 text-base text-content-secondary", className)} {...props}>
        {children || "No options found"}
    </Text>
);
ComboboxEmpty.displayName = COMBOBOX_EMPTY;

const ComboboxLoading: FC<ComboboxLoadingProps> = ({ children, className, ...props }) => (
    <Text className={cn("select-none px-3 py-2 text-base text-content-secondary", className)} {...props}>
        {children || "Loading…"}
    </Text>
);
ComboboxLoading.displayName = COMBOBOX_LOADING;

const ComboboxValue: FC<
    Omit<React.ComponentProps<"span">, "children"> & {
        children?: React.ReactNode | ((value: string) => React.ReactNode);
    }
> = ({ children, ...props }) => {
    const { state } = useComboboxContext("ComboboxValue");
    const value = state.selectedKey != null ? String(state.selectedKey) : "";
    if (!children) return <ComboboxValueLabel {...props}>{value}</ComboboxValueLabel>;
    if (typeof children === "function") return children(value);
    return children;
};
ComboboxValue.displayName = COMBOBOX_VALUE;

const ComboboxValueLabel: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => (
    <span className={cn("truncate text-base text-content-primary", className)} {...props}>
        {children}
    </span>
);
ComboboxValueLabel.displayName = "Combobox.ValueLabel";

const ComboboxFooter: FC<React.ComponentProps<"div">> = ({ children, className, ...props }) => (
    <div className={cn("border-t border-border-base px-3 py-2", className)} {...props}>
        {children}
    </div>
);
ComboboxFooter.displayName = "Combobox.Footer";

Combobox.Popover = ComboboxPopover;
Combobox.List = ComboboxList;
Combobox.ListItem = ComboboxListItem;
Combobox.ListItemTitle = ComboboxListItemTitle;
Combobox.ListItemDescription = ComboboxListItemDescription;
Combobox.ListItemLeadingIcon = ComboboxListItemLeadingIcon;
Combobox.Empty = ComboboxEmpty;
Combobox.Value = ComboboxValue;
Combobox.ValueLabel = ComboboxValueLabel;
Combobox.Loading = ComboboxLoading;
Combobox.SearchBox = ComboboxSearchBox;
Combobox.Footer = ComboboxFooter;

export { Combobox };
